import { useState, useEffect, useCallback } from 'react';
import type { EggView } from '@/types/rewards';

/**
 * Ticks every 500ms and returns a progress map (0..1) for each egg.
 * On visibility change (tab focus), recomputes immediately.
 */
export function useEggProgress(eggs: EggView[]): Map<string, number> {
  const compute = useCallback(() => {
    const now = Date.now();
    const map = new Map<string, number>();
    for (const egg of eggs) {
      if (egg.hatchedAt) {
        map.set(egg.id, 1);
        continue;
      }
      const elapsed = now - new Date(egg.startedAt).getTime();
      map.set(egg.id, Math.min(elapsed / egg.duration, 1));
    }
    return map;
  }, [eggs]);

  const [progressMap, setProgressMap] = useState(() => compute());

  useEffect(() => {
    setProgressMap(compute());
    const id = setInterval(() => setProgressMap(compute()), 500);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        setProgressMap(compute());
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [compute]);

  return progressMap;
}
